import { Employee } from '../types/employee';
import { collection, doc, updateDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config.ts';

export interface TDLRLicenseInfo {
  licenseNumber: string;
  name: string;
  expirationDate: Date | null;
  licenseType: string;
  status: string;
  county: string;
  lastChecked: Date;
  error?: string;
}

export interface TDLRCheckResult {
  success: boolean;
  licenseInfo?: TDLRLicenseInfo;
  error?: string;
}

export class TDLRChecker {
  private static readonly TDLR_SEARCH_URL = 'https://www.tdlr.texas.gov/LicenseSearch/';
  private static readonly CORS_PROXY = 'https://api.allorigins.win/raw?url=';

  /**
   * Check a single employee's TDLR license and update their record if successful
   */
  static async checkAndUpdateEmployeeLicense(employeeId: string, licenseNumber: string): Promise<TDLRCheckResult> {
    try {
      const result = await this.checkLicense(licenseNumber);
      
      if (result.success && result.licenseInfo && result.licenseInfo.expirationDate) {
        // Update the employee's record in Firebase
        await updateDoc(doc(db, 'employees', employeeId), {
          tdlrExpires: result.licenseInfo.expirationDate,
          updatedAt: new Date()
        });
      }
      
      return result;
    } catch (error) {
      console.error('Error checking and updating employee license:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Check a single TDLR license number and return license information
   */
  static async checkLicense(licenseNumber: string): Promise<TDLRCheckResult> {
    try {
      // Clean the license number (remove any non-numeric characters)
      const cleanLicenseNumber = licenseNumber.replace(/\D/g, '');
      
      if (!cleanLicenseNumber) {
        return {
          success: false,
          error: 'Invalid license number format'
        };
      }

      // Format license number with leading zeros (TDLR expects 8 digits)
      const paddedLicenseNumber = cleanLicenseNumber.padStart(8, '0');
      
      // Construct the direct URL to the license details
      const licenseUrl = `https://www.tdlr.texas.gov/LicenseSearch/SearchResultDetail.asp?1=APPELE${paddedLicenseNumber}&2=AE`;
      
      // Try multiple proxy services - corsproxy.io first since it's working reliably
      const proxyServices = [
        `https://corsproxy.io/?${encodeURIComponent(licenseUrl)}`,
        `https://api.allorigins.win/get?url=${encodeURIComponent(licenseUrl)}`,
        `https://cors-anywhere.herokuapp.com/${licenseUrl}`
      ];

      let lastError: Error | null = null;

      for (let i = 0; i < proxyServices.length; i++) {
        try {
          console.log(`Trying proxy service ${i + 1}/${proxyServices.length} for license ${cleanLicenseNumber}`);
          
          const proxyUrl = proxyServices[i];
          const response = await fetch(proxyUrl, {
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
            }
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          let html: string;
          
          // Handle different proxy response formats
          if (proxyUrl.includes('allorigins.win')) {
            const data = await response.json();
            html = data.contents;
          } else {
            // corsproxy.io and cors-anywhere return plain HTML
            html = await response.text();
          }
          
          const licenseInfo = this.parseHTMLResponse(html, cleanLicenseNumber);
          
          if (licenseInfo.name && licenseInfo.expirationDate) {
            console.log(`Successfully retrieved license info for ${cleanLicenseNumber} using proxy ${i + 1}`);
            return {
              success: true,
              licenseInfo
            };
          } else if (licenseInfo.error) {
            // If we got a valid response but no license found, don't try other proxies
            return {
              success: false,
              error: licenseInfo.error
            };
          }
          
        } catch (fetchError) {
          console.warn(`Proxy service ${i + 1} failed for license ${cleanLicenseNumber}:`, fetchError);
          lastError = fetchError instanceof Error ? fetchError : new Error(String(fetchError));
          
          // Add minimal delay between proxy attempts (only if corsproxy fails)
          if (i < proxyServices.length - 1 && i > 0) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }

      // All proxies failed, fall back to manual verification
      console.warn(`All proxy services failed for license ${cleanLicenseNumber}`);
      
      const licenseInfo: TDLRLicenseInfo = {
        licenseNumber: cleanLicenseNumber,
        name: '',
        expirationDate: null,
        licenseType: 'AE',
        status: '',
        county: '',
        lastChecked: new Date(),
        error: `Automatic lookup failed. Please verify license ${cleanLicenseNumber} manually at: ${licenseUrl}`
      };

      return {
        success: false,
        licenseInfo,
        error: `All proxy services failed. Last error: ${lastError?.message || 'Unknown error'}. Manual verification required.`
      };

    } catch (error) {
      console.error('Error checking TDLR license:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Parse the HTML response from TDLR website to extract license information
   */
  private static parseHTMLResponse(html: string, licenseNumber: string): TDLRLicenseInfo {
    const licenseInfo: TDLRLicenseInfo = {
      licenseNumber,
      name: '',
      expirationDate: null,
      licenseType: '',
      status: '',
      county: '',
      lastChecked: new Date()
    };

    try {
      console.log('Parsing HTML response for license:', licenseNumber);

      // Check if no license found
      if (html.includes('If license not found') || html.includes('No license found')) {
        licenseInfo.error = 'License not found in TDLR database';
        return licenseInfo;
      }

      // Extract name - look for pattern like "CONTRERAS, LUCAS S" or "ORTIZ, CHRISTIAN"
      const nameMatch = html.match(/([A-Z]+(?:,\s*[A-Z\s]+)*)\s*(?:<\/td>|County:)/i);
      if (nameMatch) {
        licenseInfo.name = nameMatch[1].trim().replace(/\s+/g, ' ');
      }

      // Extract county - look for "County: COLLIN" or "County: DALLAS"
      const countyMatch = html.match(/County:\s*([A-Z\s]+)/i);
      if (countyMatch) {
        licenseInfo.county = countyMatch[1].trim();
      }

      // Extract license type - look for "Electrical Apprentice"
      const typeMatch = html.match(/(Electrical\s+Apprentice|Electrical\s+[A-Z][a-z]+)/i);
      if (typeMatch) {
        licenseInfo.licenseType = typeMatch[1].trim();
      } else {
        // Default for AE license type
        licenseInfo.licenseType = 'Electrical Apprentice';
      }

      // Extract license number from response - look for "License #: 691694"
      const licenseNumberMatch = html.match(/License\s*#:\s*(\d+)/i);
      if (licenseNumberMatch) {
        const foundNumber = licenseNumberMatch[1];
        // Update the license number to match what's in the database
        licenseInfo.licenseNumber = foundNumber;
      }

      // Extract expiration date - look for both "Expiron Date:" and "Expiration Date:"
      const expirationMatch = html.match(/Expir(?:on|ation)\s+Date:\s*(\d{2}\/\d{2}\/\d{4})/i);
      if (expirationMatch) {
        const dateStr = expirationMatch[1];
        licenseInfo.expirationDate = new Date(dateStr);
      }

      // Extract status - look for "License Status: Active"
      const statusMatch = html.match(/License\s+Status:\s*([A-Za-z\s]+)/i);
      if (statusMatch) {
        licenseInfo.status = statusMatch[1].trim();
      }

      // If we don't have essential info, it might be a parsing error
      if (!licenseInfo.name && !licenseInfo.expirationDate) {
        console.warn('Failed to parse essential license information from HTML');
        licenseInfo.error = 'Failed to parse license information from TDLR response';
      }

    } catch (error) {
      console.error('Error parsing TDLR response:', error);
      licenseInfo.error = 'Error parsing TDLR response';
    }

    return licenseInfo;
  }

  /**
   * Check all employees with TDLR numbers and update their expiration dates
   */
  static async checkAllEmployeeLicenses(): Promise<{
    checked: number;
    updated: number;
    errors: Array<{ employee: string; error: string }>;
  }> {
    const results = {
      checked: 0,
      updated: 0,
      errors: [] as Array<{ employee: string; error: string }>
    };

    try {
      // Get all employees with TDLR numbers
      const employeesRef = collection(db, 'employees');
      const q = query(employeesRef, where('tdlrNumber', '!=', null));
      const snapshot = await getDocs(q);

      const employees = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Employee[];

      // Check each employee's TDLR license
      for (const employee of employees) {
        if (!employee.tdlrNumber) continue;

        results.checked++;

        try {
          const checkResult = await this.checkLicense(employee.tdlrNumber);
          
          if (checkResult.success && checkResult.licenseInfo) {
            const { licenseInfo } = checkResult;
            
            // Update the employee's TDLR expiration date if we found one
            if (licenseInfo.expirationDate) {
              const existingExpiration = employee.tdlrExpires ? 
                (employee.tdlrExpires instanceof Date ? employee.tdlrExpires : employee.tdlrExpires.toDate()) : 
                null;
              
              // Update if date is different or missing
              if (!existingExpiration || 
                  licenseInfo.expirationDate.getTime() !== existingExpiration.getTime()) {
                
                await updateDoc(doc(db, 'employees', employee.id), {
                  tdlrExpires: licenseInfo.expirationDate,
                  updatedAt: new Date()
                });
                
                results.updated++;
                console.log(`Updated TDLR expiration for ${employee.name}: ${licenseInfo.expirationDate.toLocaleDateString()}`);
              } else {
                console.log(`TDLR expiration already up to date for ${employee.name}`);
              }
            } else {
              results.errors.push({
                employee: employee.name || 'Unknown',
                error: 'No expiration date found in TDLR response'
              });
            }
          } else {
            results.errors.push({
              employee: employee.name || 'Unknown',
              error: checkResult.error || 'Unknown error'
            });
          }

          // Add a small delay to avoid overwhelming the TDLR server
          await new Promise(resolve => setTimeout(resolve, 1000));

        } catch (error) {
          results.errors.push({
            employee: employee.name || 'Unknown',
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      }

    } catch (error) {
      console.error('Error checking employee licenses:', error);
      results.errors.push({
        employee: 'System',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }

    return results;
  }

  /**
   * Get employees with TDLR licenses expiring within the specified number of days
   */
  static async getExpiringLicenses(daysAhead: number = 30): Promise<Employee[]> {
    try {
      const employeesRef = collection(db, 'employees');
      const snapshot = await getDocs(employeesRef);
      
      const employees = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        tdlrExpires: doc.data().tdlrExpires?.toDate()
      })) as Employee[];

      const now = new Date();
      const futureDate = new Date(now.getTime() + (daysAhead * 24 * 60 * 60 * 1000));

      return employees.filter(employee => 
        employee.tdlrNumber && 
        employee.tdlrExpires && 
        employee.tdlrExpires > now && 
        employee.tdlrExpires <= futureDate &&
        employee.status === 'active'
      );

    } catch (error) {
      console.error('Error getting expiring licenses:', error);
      return [];
    }
  }

  /**
   * Get employees with expired TDLR licenses
   */
  static async getExpiredLicenses(): Promise<Employee[]> {
    try {
      const employeesRef = collection(db, 'employees');
      const snapshot = await getDocs(employeesRef);
      
      const employees = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        tdlrExpires: doc.data().tdlrExpires?.toDate()
      })) as Employee[];

      const now = new Date();

      return employees.filter(employee => 
        employee.tdlrNumber && 
        employee.tdlrExpires && 
        employee.tdlrExpires < now &&
        employee.status === 'active'
      );

    } catch (error) {
      console.error('Error getting expired licenses:', error);
      return [];
    }
  }

  /**
   * Alternative method using a web scraping service (if CORS issues persist)
   */
  static async checkLicenseWithScraper(licenseNumber: string): Promise<TDLRCheckResult> {
    try {
      // This would require a backend service or serverless function
      // For now, we'll return a placeholder implementation
      console.warn('Web scraper method not implemented. Consider setting up a backend service.');
      
      return {
        success: false,
        error: 'Web scraper method requires backend implementation'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
}

export default TDLRChecker;
